/**
 * Unified Media Cache Manager
 * 
 * Orchestrates all cache layers (Memory, IndexedDB, Service Worker, network)
 * to provide a single API for media caching with automatic fallback.
 * 
 * Cache Layer Priority:
 * 1. Memory Cache - Fastest, session-only (50MB)
 * 2. IndexedDB Cache - Persistent, browser storage (500MB, 7-day TTL)
 * 3. Service Worker Cache - Network-level, offline support (200MB, 3-day TTL)
 * 4. Network (Firebase Storage) - Source of truth, always available
 */

import { memoryCache } from './memoryCache';
import { indexedDBCache } from './indexedDBCache';
import { serviceWorkerManager } from './serviceWorkerManager';
import { logger } from '../logger';

export interface CacheStats {
    memory: {
        entries: number;
        size: number;
        maxSize: number;
        utilizationPercent: number;
    };
    indexedDB: {
        entries: number;
        totalSize: number;
        maxSize: number;
        utilizationPercent: number;
    } | null;
    serviceWorker: {
        entries: number;
        totalSize: number;
        maxSize: number;
        status: string;
    } | null;
    totalCachedSize: number;
}

export interface CacheHitInfo {
    source: 'memory' | 'indexedDB' | 'serviceWorker' | 'network';
    data: string;
    cached: boolean;
}

class MediaCacheManager {
    private initialized: boolean = false;
    private initPromise: Promise<void> | null = null;

    /**
     * Initialize all cache layers
     */
    async initialize(): Promise<void> {
        if (this.initialized) return;
        
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = (async () => {
            console.log('[MediaCacheManager] Initializing cache layers...');
            
            // Memory cache is always ready (synchronous)
            console.log('[MediaCacheManager] ✅ Memory cache ready');

            // IndexedDB initializes lazily on first use
            console.log('[MediaCacheManager] ✅ IndexedDB cache ready');

            // Register service worker
            try {
                const swRegistered = await serviceWorkerManager.register();
                if (swRegistered) {
                    console.log('[MediaCacheManager] ✅ Service worker registered');
                } else {
                    console.log('[MediaCacheManager] ⚠️ Service worker not available');
                }
            } catch (error) {
                console.warn('[MediaCacheManager] Service worker registration failed:', error);
            }

            this.initialized = true;
            console.log('[MediaCacheManager] All cache layers initialized');
        })();

        return this.initPromise;
    }

    /**
     * Get media from cache, checking all layers
     * Returns null if not found in any cache layer
     */
    async get(key: string): Promise<string | null> {
        await this.initialize();

        // Layer 1: Memory Cache (fastest)
        const memoryResult = memoryCache.get(key);
        if (memoryResult) {
            logger.debug('Cache', `Memory HIT: ${key}`);
            return memoryResult;
        }

        // Layer 2: IndexedDB Cache
        const indexedDBResult = await indexedDBCache.get(key);
        if (indexedDBResult) {
            logger.debug('Cache', `IndexedDB HIT: ${key}`);
            // Promote to memory cache for faster subsequent access
            this.promoteToMemory(key, indexedDBResult);
            return indexedDBResult;
        }

        // Layer 3: Service Worker Cache
        // Service worker intercepts fetch requests automatically
        // This layer is handled at the network level

        logger.debug('Cache', `MISS: ${key}`);
        return null;
    }

    /**
     * Get media with full hit information
     */
    async getWithInfo(key: string): Promise<CacheHitInfo | null> {
        await this.initialize();

        // Layer 1: Memory Cache
        const memoryResult = memoryCache.get(key);
        if (memoryResult) {
            return { source: 'memory', data: memoryResult, cached: true };
        }

        // Layer 2: IndexedDB Cache
        const indexedDBResult = await indexedDBCache.get(key);
        if (indexedDBResult) {
            this.promoteToMemory(key, indexedDBResult);
            return { source: 'indexedDB', data: indexedDBResult, cached: true };
        }

        return null;
    }

    /**
     * Store media in all applicable cache layers
     */
    async set(key: string, data: string, contentType: string = 'application/octet-stream'): Promise<void> {
        await this.initialize();

        logger.debug('Cache', `Storing: ${key}`);

        // Store in all layers simultaneously
        const promises: Promise<void>[] = [];

        // Layer 1: Memory Cache (sync, but wrap for consistency)
        promises.push(Promise.resolve().then(() => {
            memoryCache.set(key, data, contentType);
        }));

        // Layer 2: IndexedDB Cache
        promises.push(indexedDBCache.set(key, data, contentType));

        // Layer 3: Service Worker handles its own caching via fetch intercept

        await Promise.allSettled(promises);
        logger.debug('Cache', `Stored in all layers: ${key}`);
    }

    /**
     * Remove media from all cache layers
     */
    async delete(key: string): Promise<void> {
        await this.initialize();

        logger.debug('Cache', `Deleting: ${key}`);

        // Delete from all layers
        memoryCache.delete(key);
        await indexedDBCache.delete(key);

        logger.debug('Cache', `Deleted from all layers: ${key}`);
    }

    /**
     * Check if media exists in any cache layer
     */
    async has(key: string): Promise<boolean> {
        await this.initialize();

        if (memoryCache.has(key)) return true;
        if (await indexedDBCache.has(key)) return true;
        
        return false;
    }

    /**
     * Clear all cache layers
     */
    async clearAll(): Promise<void> {
        await this.initialize();

        console.log('[MediaCacheManager] Clearing all caches...');

        memoryCache.clear();
        await indexedDBCache.clear();
        await serviceWorkerManager.clearCache();

        console.log('[MediaCacheManager] All caches cleared');
    }

    /**
     * Clear only memory cache (for memory pressure)
     */
    clearMemory(): void {
        memoryCache.clear();
        console.log('[MediaCacheManager] Memory cache cleared');
    }

    /**
     * Get statistics from all cache layers
     */
    async getStats(): Promise<CacheStats> {
        await this.initialize();

        const memoryStats = memoryCache.getStats();
        const indexedDBStats = await indexedDBCache.getStats();
        const swStats = await serviceWorkerManager.getCacheStats();

        const stats: CacheStats = {
            memory: memoryStats,
            indexedDB: indexedDBStats ? {
                ...indexedDBStats,
                utilizationPercent: indexedDBStats.utilizationPercent,
            } : null,
            serviceWorker: swStats ? {
                ...swStats,
                status: serviceWorkerManager.getStatus(),
            } : null,
            totalCachedSize: memoryStats.size + 
                (indexedDBStats?.totalSize || 0) + 
                (swStats?.totalSize || 0),
        };

        return stats;
    }

    /**
     * Prefetch a list of media references into cache
     */
    async prefetch(mediaRefs: string[], fetchFn: (ref: string) => Promise<string>): Promise<void> {
        await this.initialize();

        console.log(`[MediaCacheManager] Prefetching ${mediaRefs.length} items...`);

        for (const ref of mediaRefs) {
            // Skip if already cached
            if (await this.has(ref)) {
                continue;
            }

            try {
                const data = await fetchFn(ref);
                await this.set(ref, data);
            } catch (error) {
                console.warn(`[MediaCacheManager] Prefetch failed for ${ref}:`, error);
            }
        }

        console.log('[MediaCacheManager] Prefetch complete');
    }

    /**
     * Promote data from slower cache to memory cache
     */
    private promoteToMemory(key: string, data: string): void {
        // Extract content type if it's a data URL
        const matches = data.match(/^data:([^;]+);/);
        const contentType = matches?.[1] || 'application/octet-stream';
        memoryCache.set(key, data, contentType);
    }

    /**
     * Get cache layer status
     */
    getLayerStatus(): {
        memory: boolean;
        indexedDB: boolean;
        serviceWorker: boolean;
    } {
        return {
            memory: true, // Always available
            indexedDB: typeof indexedDB !== 'undefined',
            serviceWorker: 'serviceWorker' in navigator && serviceWorkerManager.isActive(),
        };
    }
}

// Singleton instance
export const mediaCacheManager = new MediaCacheManager();

// Export class for testing
export { MediaCacheManager };

// Export individual cache layers for direct access if needed
export { memoryCache, indexedDBCache, serviceWorkerManager };
